/**
 * Searchable dropdown for selecting redaction reasons.
 * Provides client-side filtering with search and shows description.
 */

import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import type { RedactionReason } from '../../types';
import { cn } from '../ui/utils';

interface SearchableReasonSelectProps {
  reasons: RedactionReason[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function SearchableReasonSelect({
  reasons,
  value,
  onChange,
  placeholder = 'Select reason...',
}: SearchableReasonSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter reasons based on search
  const filteredReasons = reasons.filter((reason) => {
    const searchLower = search.toLowerCase();
    return (
      reason.title.toLowerCase().includes(searchLower) ||
      (reason.description && reason.description.toLowerCase().includes(searchLower))
    );
  });

  // Find selected reason
  const selectedReason = reasons.find((r) => r.title === value);

  // Focus search input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleSelect = (reason: RedactionReason) => {
    onChange(reason.title);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center justify-between h-7 w-full px-2 text-xs border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors text-left',
            !value && 'text-gray-400'
          )}
        >
          <span className="truncate">
            {selectedReason ? selectedReason.title : placeholder}
          </span>
          <ChevronDown className="h-3 w-3 ml-1 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 bg-white border border-gray-200 shadow-lg rounded-lg"
        align="start"
        sideOffset={4}
      >
        {/* Search input */}
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reasons..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Reasons list */}
        <div className="max-h-[200px] overflow-y-auto">
          {filteredReasons.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              No reasons found
            </div>
          ) : (
            <div className="py-1">
              {filteredReasons.map((reason) => {
                const isSelected = reason.title === value;
                return (
                  <button
                    key={reason.id}
                    type="button"
                    onClick={() => handleSelect(reason)}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-start gap-2',
                      isSelected && 'bg-gray-50'
                    )}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isSelected ? (
                        <Check className="h-3.5 w-3.5 text-gray-900" />
                      ) : (
                        <div className="h-3.5 w-3.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {reason.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {reason.description && reason.description.trim() !== ''
                          ? reason.description
                          : <span className="italic text-gray-400">No description</span>
                        }
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Clear selection option */}
        {value && (
          <div className="border-t border-gray-100 p-1">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 rounded transition-colors"
            >
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
